import { axiosAutoPassErrorConverter } from '@autopass-web/mochi-utils';

import client from '../services/client';
import { checkIfApplePaySupported } from './check-if-apple-pay-supported';

import type { ChainStoreInvoice, SupportReceiptType } from '../types';

let applePaySession;

export async function createApplePayTransaction(
  {
    onSuccess,
    onError,
    onCancel,
  }: {
    onSuccess: (data: {
      chainStoreName: string;
      amount: string;
      callbackUrl: string | null;
    }) => void;
    onError: (message: string) => void;
    onCancel: () => void;
  },
  {
    invoice,
    receipt,
  }: {
    receipt: {
      name: string;
      phone: string;
      barcode?: string;
      type: SupportReceiptType;
      ubn?: string;
      donate?: string;
    };
    invoice: ChainStoreInvoice;
  },
) {
  if (applePaySession) {
    return;
  }

  if (!checkIfApplePaySupported()) {
    console.warn('apple pay unavailable');
    return;
  }

  let cancel;
  const { amount, capture_method } = invoice;

  const APPLE_PAY_CONFIG: ApplePayJS.ApplePayPaymentRequest = {
    countryCode: 'TW',
    currencyCode: 'TWD',
    merchantCapabilities: ['supports3DS', 'supportsDebit', 'supportsCredit'],
    supportedNetworks: ['visa', 'masterCard', 'jcb'],
    total: {
      label: '車麻吉',
      amount,
      type: 'final',
    },
    requiredShippingContactFields: ['email'],
  };

  applePaySession = new ApplePaySession(2, APPLE_PAY_CONFIG);

  applePaySession.onvalidatemerchant = async (e) => {
    const validationUrl = e.validationURL;

    try {
      const { data } = await client.createApplePaySessionValidation({
        sessionUrl: validationUrl,
      });

      if (cancel) return;

      applePaySession.completeMerchantValidation(data.data);
    } catch (error) {
      const err = axiosAutoPassErrorConverter(error);
      if (err == null) return;

      onError(err.message);
    }
  };

  applePaySession.onpaymentauthorized = async (e) => {
    const {
      shippingContact,
      token: { paymentData },
    } = e.payment;

    const service =
      capture_method === 'automatic'
        ? client.confirmApplePayDirectPayTransaction
        : client.confirmApplePayAuthorizedTransaction;
    try {
      const {
        data: { data },
      } = await service({
        receipt,
        email: shippingContact?.emailAddress,
        applePayPaymentInfo: JSON.stringify(paymentData),
        invoiceId: invoice.id,
      });

      if (data.payment_state === 'unpaid') {
        applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
        return;
      }

      applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
      onSuccess({
        chainStoreName: data.chain_store_name,
        amount: data.amount,
        callbackUrl: data.callback_url,
      });
    } catch (error) {
      applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);

      const err = axiosAutoPassErrorConverter(error);
      if (err == null) return;

      onError(err.message);
    }
  };

  applePaySession.oncancel = async (e) => {
    cancel = e;
    onCancel();
  };

  try {
    await applePaySession.begin();
  } catch (error) {
    const err = axiosAutoPassErrorConverter(error);
    if (err == null) return;

    onError(err.message);
  }
}
